/** Auth handler host for Firebase JS SDK (must match the Google OAuth client). */

function hostOnly(raw?: string): string {
  let h = (raw ?? '').trim().toLowerCase().replace(/\.$/, '');
  h = h.replace(/^https?:\/\//, '');
  const slash = h.indexOf('/');
  if (slash >= 0) h = h.slice(0, slash);
  const colon = h.indexOf(':');
  if (colon >= 0) h = h.slice(0, colon);
  return h;
}

/**
 * One authDomain per Firebase project — the URI registered on the Google OAuth
 * Web client (`https://{authDomain}/__/auth/handler`). Incubator slugs must not
 * override this with `{slug}.communiosocial.com` (redirect_uri_mismatch).
 * Product slugs never register per-slug Auth domains. Apex Google uses
 * communiosocial.com/__/auth/handler. Slugs bounce to apex — never ops ITSM.
 */
export function resolveFirebaseAuthDomain(opts: {
  hostname?: string;
  envAuthDomain?: string;
}): string {
  return (opts.envAuthDomain ?? '').trim();
}

export function isCommunioProductSlugHost(host: string): boolean {
  const h = hostOnly(host);
  return (
    h.endsWith('.communiosocial.com') &&
    h !== 'ops.communiosocial.com' &&
    h !== 'www.communiosocial.com'
  );
}

export function isCommunioApexHost(host: string): boolean {
  const h = hostOnly(host);
  return h === 'communiosocial.com' || h === 'www.communiosocial.com';
}

function isOpsAuthDomain(auth: string): boolean {
  return auth === 'ops.communiosocial.com';
}

/** Slugs bounce to ops only when baked authDomain is ops. Apex Google stays on communiosocial.com. */
function usesOpsGoogleHandler(page: string): boolean {
  return isCommunioProductSlugHost(page);
}

/**
 * Firebase signInWithRedirect from the page origin. Product slugs are false:
 * that flow requires per-slug authorizedDomains, which we do not create.
 */
export function oauthUsesRedirect(opts: {
  hostname?: string;
  authDomain?: string;
}): boolean {
  const page = hostOnly(opts.hostname);
  const auth = hostOnly(opts.authDomain);
  if (!page || !auth) return false;
  if (usesOpsGoogleHandler(page)) return false;
  return page === auth;
}

/** Product slug Google when authDomain is ops: leave the slug and sign in on ops. Apex stays on-site. */
export function productAuthUsesOpsHost(opts: {
  hostname?: string;
  authDomain?: string;
}): boolean {
  const page = hostOnly(opts.hostname);
  const auth = hostOnly(opts.authDomain);
  return usesOpsGoogleHandler(page) && isOpsAuthDomain(auth);
}

/**
 * Incubator slugs are not Google JS origins. Bounce to apex (authorized origin)
 * so the account picker can open, then authReturn sends the user back.
 */
export function productSlugBouncesGoogleToApex(opts: {
  hostname?: string;
  authDomain?: string;
}): boolean {
  const page = hostOnly(opts.hostname);
  const auth = hostOnly(opts.authDomain);
  return isCommunioProductSlugHost(page) && !isOpsAuthDomain(auth);
}

export function apexLoginUrl(
  returnTo: string,
  opts?: { theme?: { fontSize: string; primaryHue: string; bg: string } | null },
): string {
  const u = new URL('https://communiosocial.com/login/');
  u.searchParams.set('authReturn', authReturnWithoutHash(returnTo));
  if (opts?.theme) {
    u.searchParams.set(
      'ctheme',
      encodeURIComponent(
        JSON.stringify({
          fontSize: opts.theme.fontSize,
          primaryHue: opts.theme.primaryHue,
          bg: opts.theme.bg,
        }),
      ),
    );
  }
  return u.toString();
}

/**
 * authReturn must never carry #communio_id_token=… — that nests the bounce token in the
 * query string and causes apex↔slug redirect loops.
 */
export function authReturnWithoutHash(returnTo: string): string {
  const raw = (returnTo || '').trim();
  if (!raw) return raw;
  try {
    const u = new URL(raw);
    u.hash = '';
    return u.toString();
  } catch {
    const i = raw.indexOf('#');
    return i >= 0 ? raw.slice(0, i) : raw;
  }
}

/**
 * @deprecated Auth is modal + API on the current host. Never page-navigate to apex.
 * Kept as a no-op so old callers cannot reintroduce a full-page bounce.
 */
export function slugAuthBounceHref(_opts: {
  hostname?: string;
  authDomain?: string;
  returnTo: string;
  opsOrigin?: string;
  theme?: { fontSize: string; primaryHue: string; bg: string } | null;
}): string | null {
  return null;
}

export function opsAuthOrigin(authApiUrl?: string): string {
  const raw = (authApiUrl ?? '').trim() || 'https://ops.communiosocial.com/api';
  try {
    return new URL(raw).origin;
  } catch {
    return 'https://ops.communiosocial.com';
  }
}

export function opsLoginUrl(returnTo: string, opsOrigin: string): string {
  const origin = opsOrigin.replace(/\/$/, '');
  return `${origin}/login/?authReturn=${encodeURIComponent(authReturnWithoutHash(returnTo))}`;
}

/**
 * Absolute https product/apex URL, or same-site path (e.g. /setup/?slug=x) resolved on apex.
 * Relative paths used to fail allowlist → login fell back to /saas/start/.
 */
export function normalizeOpsAuthReturn(url: string, apexOrigin = 'https://communiosocial.com'): string | null {
  const raw = authReturnWithoutHash((url || '').trim());
  if (!raw) return null;
  try {
    const u = raw.startsWith('/') ? new URL(raw, apexOrigin) : new URL(raw);
    if (u.protocol !== 'https:') return null;
    if (!(isCommunioProductSlugHost(u.hostname) || isCommunioApexHost(u.hostname))) return null;
    u.hash = '';
    return u.toString();
  } catch {
    return null;
  }
}

export function isAllowedOpsAuthReturn(url: string): boolean {
  return normalizeOpsAuthReturn(url) != null;
}
